/**
 * BudgetLineChart Component
 * 
 * A D3.js-powered line chart that displays budgeted vs actual performance data.
 * Features color-coded lines (green for better than budget, red for worse than budget).
 * 
 * Props:
 * - data: Object with 'budgeted' and 'actual' arrays
 *   Each array should contain objects with 'period' and 'value' properties
 *   Period 0 = Start, Period 1 = Jan, Period 2 = Feb, etc.
 * - width: Chart width in pixels (default: 800)
 * - height: Chart height in pixels (default: 400)  
 * - title: Chart title string (default: "Budget vs Actual")
 * 
 * Data format example:
 * {
 *   "budgeted": [
 *     {"period": 0, "value": 0},
 *     {"period": 1, "value": 557312.16}, // January
 *     ...
 *   ],
 *   "actual": [
 *     {"period": 0, "value": 0},
 *     {"period": 1, "value": 1280745}, // January
 *     ...
 *   ]
 * }
 * 
 * Features:
 * - Interactive tooltips on hover with month names
 * - Color-coded performance indicators
 * - Connects actual line to period 0 (Start)
 * - Responsive design
 * - Professional styling with animations
 * - Legend showing line meanings
 * - Grid lines for easy reading
 * 
 * Usage:
 * <BudgetLineChart 
 *   data={yourData} 
 *   width={1000} 
 *   height={500} 
 *   title="Your Custom Title" 
 * />
 */

import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import './BudgetLineChart.css';

const BudgetLineChart = ({ data, width = 800, height = 400, title = "Budget vs Actual" }) => {
    const svgRef = useRef();
    const [hoveredPoint, setHoveredPoint] = useState(null);

    useEffect(() => {
        if (!data || !data.budgeted || !data.actual) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove(); // Clear previous chart

        // Set up dimensions and margins
        const margin = { top: 40, right: 80, bottom: 60, left: 100 };
        const innerWidth = width - margin.left - margin.right;
        const innerHeight = height - margin.top - margin.bottom;

        // Create main chart group
        const chartGroup = svg
            .attr('width', width)
            .attr('height', height)
            .append('g')
            .attr('transform', `translate(${margin.left}, ${margin.top})`);

        // Set up scales
        const maxPeriod = Math.max(
            d3.max(data.budgeted, d => d.period),
            d3.max(data.actual, d => d.period)
        );

        // Calculate min and max values including negatives
        const maxValue = Math.max(
            d3.max(data.budgeted, d => d.value),
            d3.max(data.actual, d => d.value)
        );
        
        const minValue = Math.min(
            d3.min(data.budgeted, d => d.value),
            d3.min(data.actual, d => d.value)
        );

        const xScale = d3.scaleLinear()
            .domain([0, maxPeriod])
            .range([0, innerWidth]);

        // Adjust scale to handle negative values with padding
        const padding = Math.abs(maxValue - minValue) * 0.1 || Math.abs(maxValue) * 0.1 || 1000;
        const yScale = d3.scaleLinear()
            .domain([minValue - padding, maxValue + padding])
            .range([innerHeight, 0]);

        // Create line generators
        const line = d3.line()
            .x(d => xScale(d.period))
            .y(d => yScale(d.value))
            .curve(d3.curveMonotoneX);

        // Add grid lines
        const xAxis = d3.axisBottom(xScale)
            .tickSize(-innerHeight)
            .tickFormat('');

        const yAxis = d3.axisLeft(yScale)
            .tickSize(-innerWidth)
            .tickFormat('');

        chartGroup.append('g')
            .attr('class', 'grid')
            .attr('transform', `translate(0, ${innerHeight})`)
            .call(xAxis);

        chartGroup.append('g')
            .attr('class', 'grid')
            .call(yAxis);

        // Add zero line if we have negative values
        if (minValue < 0) {
            chartGroup.append('line')
                .attr('x1', 0)
                .attr('x2', innerWidth)
                .attr('y1', yScale(0))
                .attr('y2', yScale(0))
                .style('stroke', '#333')
                .style('stroke-width', 1.5)
                .style('stroke-dasharray', '3,3')
                .style('opacity', 0.7);
        }

        // Add axes
        chartGroup.append('g')
            .attr('transform', `translate(0, ${innerHeight})`)
            .call(d3.axisBottom(xScale)
                .tickFormat(d => {
                    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    return d === 0 ? 'Start' : months[(d - 1) % 12];
                }))
            .selectAll('text')
            .style('font-size', '12px');

        chartGroup.append('g')
            .call(d3.axisLeft(yScale)
                .tickFormat(d => {
                    if (Math.abs(d) >= 1000000) {
                        return `$${(d / 1000000).toFixed(1)}M`;
                    } else if (Math.abs(d) >= 1000) {
                        return `$${(d / 1000).toFixed(0)}K`;
                    } else {
                        return `$${d.toFixed(0)}`;
                    }
                }))
            .selectAll('text')
            .style('font-size', '12px');

        // Add title
        svg.append('text')
            .attr('x', width / 2)
            .attr('y', 25)
            .attr('text-anchor', 'middle')
            .style('font-size', '18px')
            .style('font-weight', '700')
            .style('fill', '#2c3e50')
            .text(title);

        // Draw budgeted line (light gray)
        chartGroup.append('path')
            .datum(data.budgeted)
            .attr('class', 'budget-line')
            .attr('d', line)
            .style('fill', 'none')
            .style('stroke', '#ccc')
            .style('stroke-width', 3)
            .style('opacity', 0.8);

        // Filter actual data to include all periods with data (including negatives), plus period 0
        const actualDataFiltered = data.actual.filter(d => d.value !== 0 || d.period === 0);

        // Create segments for actual line with color coding
        for (let i = 0; i < actualDataFiltered.length - 1; i++) {
            const currentPoint = actualDataFiltered[i];
            const nextPoint = actualDataFiltered[i + 1];
            
            // Find corresponding budgeted values for comparison
            const currentBudget = data.budgeted.find(d => d.period === currentPoint.period)?.value || 0;
            const nextBudget = data.budgeted.find(d => d.period === nextPoint.period)?.value || 0;
            
            // Determine color based on whether variance is negative (actual below budget)
            const nextVariance = nextPoint.value - nextBudget;
            const isVarianceNegative = nextVariance < 0;
            // Apply flip logic if data.flip is true
            const shouldUseRed = data.flip ? !isVarianceNegative : isVarianceNegative;
            const color = shouldUseRed ? '#e74c3c' : '#27ae60'; // Red if below budget (or above if flipped), green otherwise

            chartGroup.append('path')
                .datum([currentPoint, nextPoint])
                .attr('class', 'actual-line-segment')
                .attr('d', line)
                .style('fill', 'none')
                .style('stroke', color)
                .style('stroke-width', 4)
                .style('opacity', 0.9);
        }

        // Add data points for budgeted
        chartGroup.selectAll('.budget-dot')
            .data(data.budgeted)
            .enter()
            .append('circle')
            .attr('class', 'budget-dot')
            .attr('cx', d => xScale(d.period))
            .attr('cy', d => yScale(d.value))
            .attr('r', 4)
            .style('fill', '#999')
            .style('stroke', '#fff')
            .style('stroke-width', 2)
            .style('cursor', 'pointer')
            .on('mouseover', (event, d) => {
                const months = ['Start', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                               'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                setHoveredPoint({
                    period: d.period,
                    monthName: d.period === 0 ? 'Start' : months[d.period % 12] || `Month ${d.period}`,
                    budget: d.value,
                    type: 'budget',
                    flip: data.flip || false,
                    x: event.pageX,
                    y: event.pageY
                });
            })
            .on('mouseout', () => {
                setHoveredPoint(null);
            });

        // Add data points for actual (include period 0 and periods with values > 0)
        chartGroup.selectAll('.actual-dot')
            .data(actualDataFiltered)
            .enter()
            .append('circle')
            .attr('class', 'actual-dot')
            .attr('cx', d => xScale(d.period))
            .attr('cy', d => yScale(d.value))
            .attr('r', 5)
            .style('fill', d => {
                // Color red if variance is negative (actual below budget), green otherwise
                const budget = data.budgeted.find(b => b.period === d.period)?.value || 0;
                const variance = d.value - budget;
                const isVarianceNegative = variance < 0;
                // Apply flip logic if data.flip is true
                const shouldUseRed = data.flip ? !isVarianceNegative : isVarianceNegative;
                return shouldUseRed ? '#e74c3c' : '#27ae60';
            })
            .style('stroke', '#fff')
            .style('stroke-width', 2)
            .style('cursor', 'pointer')
            .on('mouseover', (event, d) => {
                const budget = data.budgeted.find(b => b.period === d.period)?.value || 0;
                const months = ['Start', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                               'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                setHoveredPoint({
                    period: d.period,
                    monthName: d.period === 0 ? 'Start' : months[d.period % 12] || `Month ${d.period}`,
                    actual: d.value,
                    budget: budget,
                    type: 'actual',
                    flip: data.flip || false,
                    x: event.pageX,
                    y: event.pageY
                });
            })
            .on('mouseout', () => {
                setHoveredPoint(null);
            });

    }, [data, width, height, title]);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const formatPercentage = (actual, budget) => {
        if (budget === 0) return 'N/A';
        const percentage = ((actual - budget) / budget) * 100;
        return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(1)}%`;
    };

    // Calculate totals for summary bar
    const calculateTotals = () => {
        if (!data || !data.budgeted || !data.actual) return null;
        
        // Find the current period (last period with actual data, including negatives)
        const actualWithData = data.actual.filter(d => d.value !== 0);
        if (actualWithData.length === 0) return null;
        
        const currentPeriod = Math.max(...actualWithData.map(d => d.period));
        const currentBudget = data.budgeted.find(d => d.period === currentPeriod)?.value || 0;
        const currentActual = data.actual.find(d => d.period === currentPeriod)?.value || 0;
        const currentVarianceDollar = currentActual - currentBudget;
        const currentVariancePercent = currentBudget === 0 ? 0 : ((currentActual - currentBudget) / currentBudget) * 100;
        
        // Year-to-date totals
        const totalBudget = data.budgeted.find(d => d.period === 12)?.value || 0;
        const totalActual = currentActual;
        const totalVarianceDollar = totalActual - totalBudget;
        const totalVariancePercent = totalBudget === 0 ? 0 : ((totalActual - totalBudget) / totalBudget) * 100;
        
        return {
            currentPeriod,
            currentBudget,
            currentActual,
            currentVarianceDollar,
            currentVariancePercent,
            totalBudget,
            totalActual,
            totalVarianceDollar,
            totalVariancePercent
        };
    };

    const totals = calculateTotals();

    return (
        <div className="budget-line-chart-container">
            <svg ref={svgRef}></svg>
            
            {/* Summary Bar */}
            {totals && (
                <div className="chart-summary-bar">
                    {/* Current Period Row */}
                    <div className="summary-row">
                        <div className="summary-row-title">
                            Current Period ({totals.currentPeriod === 0 ? 'Start' : 
                                new Date(2024, totals.currentPeriod - 1).toLocaleString('default', { month: 'short' })})
                        </div>
                        <div className="summary-row-content">
                            <div className="summary-item">
                                <div className="summary-label">Budget</div>
                                <div className="summary-value">{formatCurrency(totals.currentBudget)}</div>
                            </div>
                            <div className="summary-item">
                                <div className="summary-label">Actual</div>
                                <div className="summary-value">{formatCurrency(totals.currentActual)}</div>
                            </div>
                            <div className="summary-item">
                                <div className="summary-label">% of Budget</div>
                                <div className="summary-value">
                                    {totals.currentBudget === 0 ? 'N/A' : ((totals.currentActual / totals.currentBudget) * 100).toFixed(1)}%
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Year-to-Date Row */}
                    <div className="summary-row">
                        <div className="summary-row-title">Year-to-Date Total</div>
                        <div className="summary-row-content">
                            <div className="summary-item">
                                <div className="summary-label">Budget</div>
                                <div className="summary-value">{formatCurrency(totals.totalBudget)}</div>
                            </div>
                            <div className="summary-item">
                                <div className="summary-label">Actual</div>
                                <div className="summary-value">{formatCurrency(totals.totalActual)}</div>
                            </div>
                            <div className="summary-item">
                                <div className="summary-label">% of Budget</div>
                                <div className="summary-value">
                                    {totals.totalBudget === 0 ? 'N/A' : ((totals.totalActual / totals.totalBudget) * 100).toFixed(1)}%
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {hoveredPoint && (
                <div 
                    className="chart-tooltip"
                    style={{
                        left: hoveredPoint.x + 10,
                        top: hoveredPoint.y - 80,
                    }}
                >
                    <div className="tooltip-title">{hoveredPoint.monthName}</div>
                    {hoveredPoint.type === 'budget' ? (
                        <div className="tooltip-row">
                            <span className="tooltip-label">Budget:</span>
                            <span className="tooltip-value">{formatCurrency(hoveredPoint.budget)}</span>
                        </div>
                    ) : (
                        <>
                            <div className="tooltip-row">
                                <span className="tooltip-label">Budget:</span>
                                <span className="tooltip-value">{formatCurrency(hoveredPoint.budget)}</span>
                            </div>
                            <div className="tooltip-row">
                                <span className="tooltip-label">Actual:</span>
                                <span className="tooltip-value">{formatCurrency(hoveredPoint.actual)}</span>
                            </div>
                            <div className="tooltip-row">
                                <span className="tooltip-label">Variance:</span>
                                <span className={`tooltip-value ${(() => {
                                    const isPositiveVariance = hoveredPoint.actual >= hoveredPoint.budget;
                                    const shouldShowPositive = hoveredPoint.flip ? !isPositiveVariance : isPositiveVariance;
                                    return shouldShowPositive ? 'positive' : 'negative';
                                })()}`}>
                                    {formatCurrency(hoveredPoint.actual - hoveredPoint.budget)}
                                </span>
                            </div>
                            <div className="tooltip-row">
                                <span className="tooltip-label">Variance %:</span>
                                <span className={`tooltip-value ${(() => {
                                    const isPositiveVariance = hoveredPoint.actual >= hoveredPoint.budget;
                                    const shouldShowPositive = hoveredPoint.flip ? !isPositiveVariance : isPositiveVariance;
                                    return shouldShowPositive ? 'positive' : 'negative';
                                })()}`}>
                                    {formatPercentage(hoveredPoint.actual, hoveredPoint.budget)}
                                </span>
                            </div>
                            <div className="tooltip-row">
                                <span className="tooltip-label">% of Budget:</span>
                                <span className="tooltip-value">
                                    {hoveredPoint.budget === 0 ? 'N/A' : ((hoveredPoint.actual / hoveredPoint.budget) * 100).toFixed(1)}%
                                </span>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default BudgetLineChart;
